/**
 * ErrorState Component
 * 
 * Displays an error message with retry and cancel options.
 * For microphone permission errors, shows a dedicated friendly UI.
 * For connection/WebSocket errors, shows a user-friendly screen.
 * After 3+ connection failures, shows corporate network-specific help.
 */

import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import MicrophonePermissionScreen from './MicrophonePermissionScreen';

// Constants for retry tracking
const CONNECTION_RETRY_KEY = 'selna_connection_retry_count';
const CONNECTION_RETRY_TIMESTAMP_KEY = 'selna_connection_retry_timestamp';
const RETRY_RESET_HOURS = 1; // Reset counter after 1 hour of no errors
const CORPORATE_THRESHOLD = 3; // Show corporate screen after 3 failures

interface ErrorStateProps {
  error: Error & { source?: string; statusCode?: number; code?: string };
  onRetry: () => void;
  onCancel: () => void;
  // Microphone permission error handling
  allowKeyboardInput?: boolean;
  onContinueKeyboard?: () => void;
}

// Helper to get/set connection retry count from localStorage
function getConnectionRetryCount(): number {
  try {
    const timestamp = localStorage.getItem(CONNECTION_RETRY_TIMESTAMP_KEY);
    if (timestamp) {
      const hoursSinceLastError = (Date.now() - parseInt(timestamp, 10)) / (1000 * 60 * 60);
      if (hoursSinceLastError > RETRY_RESET_HOURS) {
        // Reset counter if it's been more than an hour
        localStorage.removeItem(CONNECTION_RETRY_KEY);
        localStorage.removeItem(CONNECTION_RETRY_TIMESTAMP_KEY);
        return 0;
      }
    }
    return parseInt(localStorage.getItem(CONNECTION_RETRY_KEY) || '0', 10);
  } catch {
    return 0;
  }
}

function incrementConnectionRetryCount(): number {
  try {
    const current = getConnectionRetryCount();
    const newCount = current + 1;
    localStorage.setItem(CONNECTION_RETRY_KEY, newCount.toString());
    localStorage.setItem(CONNECTION_RETRY_TIMESTAMP_KEY, Date.now().toString());
    return newCount;
  } catch {
    return 1;
  }
}

// Export for use by session components on successful connection
export function resetConnectionRetryCount(): void {
  try {
    localStorage.removeItem(CONNECTION_RETRY_KEY);
    localStorage.removeItem(CONNECTION_RETRY_TIMESTAMP_KEY);
  } catch {
    // Ignore errors
  }
}

// Helper function to detect if user is in a webview (WhatsApp, Messenger, Instagram, etc.)
function isInWebView(): boolean {
  const ua = navigator.userAgent.toLowerCase();
  
  // Check for common social media webviews
  const isWhatsApp = ua.includes('whatsapp');
  const isMessenger = ua.includes('fban') || ua.includes('fbav') || ua.includes('messenger');
  const isInstagram = ua.includes('instagram');
  const isFacebook = ua.includes('fb_iab') || ua.includes('fbios');
  const isLinkedIn = ua.includes('linkedin');
  const isTwitter = ua.includes('twitter');
  
  // Additional heuristic: check if it's mobile but not a known full browser
  const isMobileBrowser = /android|iphone|ipad|ipod/i.test(ua);
  const isStandaloneBrowser = ua.includes('safari') || ua.includes('chrome') || ua.includes('firefox') || ua.includes('edg');
  const mightBeWebView = isMobileBrowser && !isStandaloneBrowser;
  
  return isWhatsApp || isMessenger || isInstagram || isFacebook || isLinkedIn || isTwitter || mightBeWebView;
}

// Helper function to detect if this is a connection/WebSocket error
function isConnectionError(error: Error & { source?: string; code?: string }): boolean {
  const errorMessage = error.message?.toLowerCase() || '';
  const errorCode = (error as any).code?.toString() || '';
  const errorSource = error.source?.toLowerCase() || '';
  
  // Check for WebSocket-related errors
  const isWebSocketError = errorMessage.includes('websocket') || 
                           errorMessage.includes('connection error') ||
                           errorCode === '1006' ||
                           errorCode === 'AI_SERVICE_UNAVAILABLE';
  
  // Check for OpenAI-related connection errors
  const isOpenAIError = errorSource.includes('openai') || 
                        errorMessage.includes('ai service') ||
                        errorMessage.includes("couldn't connect");
  
  // Check for network/connection errors
  const isNetworkError = errorMessage.includes('network') ||
                         errorMessage.includes('connection') ||
                         errorMessage.includes('reconnect') ||
                         errorMessage.includes('timeout');
  
  return isWebSocketError || isOpenAIError || isNetworkError;
}

/**
 * CorporateNetworkErrorScreen - Shown after 3+ connection failures
 * Helps users in corporate environments understand the issue and contact IT
 */
function CorporateNetworkErrorScreen({
  onRetry,
  onCancel,
  retryCount
}: {
  onRetry: () => void;
  onCancel: () => void;
  retryCount: number;
}) {
  const { t } = useTranslation();
  const [showTechnicalDetails, setShowTechnicalDetails] = useState(false);
  
  // Generate mailto link for IT department
  const generateITEmail = () => {
    const subject = encodeURIComponent(t('errors.corporate.emailSubject', 'Selna Platform - Network Configuration Request'));
    const body = encodeURIComponent(t('errors.corporate.emailBody', `Hello,

I'm trying to use the Selna training platform but I'm experiencing connection issues that appear to be related to our network configuration.

The platform requires WebSocket (WSS) connections to api.openai.com for real-time voice AI functionality.

Could you please review the network requirements at:
https://selna.ai/connectivity-test/

The full IT documentation is available at:
https://github.com/user/repo/docs/IT_NETWORK_REQUIREMENTS.md

Key requirements:
- Allow HTTPS and WSS (WebSocket Secure) to api.openai.com on port 443
- Bypass SSL inspection for WebSocket connections to api.openai.com

Thank you for your help.`));
    
    return `mailto:?subject=${subject}&body=${body}`;
  };
  
  return (
    <div className="fixed inset-0 bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-6 md:p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        
        {/* Icon - Network/Building icon */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-orange-100 rounded-full mb-4">
            <svg className="w-10 h-10 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {t('errors.corporate.title', 'Corporate Network Block')}
          </h2>
          
          <p className="text-gray-600 text-base">
            {t('errors.corporate.description', "Your corporate network appears to be blocking the connection required for real-time voice conversations.")}
          </p>
        </div>
        
        {/* Main explanation */}
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-4">
          <h3 className="font-semibold text-orange-900 mb-2 flex items-center gap-2">
            <span>🏢</span>
            {t('errors.corporate.whyTitle', 'Why is this happening?')}
          </h3>
          <p className="text-sm text-orange-800">
            {t('errors.corporate.whyDescription', "Many corporate networks block WebSocket connections for security. Selna needs this to provide real-time voice conversations with AI. Your IT department can easily allow this connection.")}
          </p>
        </div>
        
        {/* Connectivity Test Link */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
          <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
            <span>🔍</span>
            {t('errors.corporate.testTitle', 'Run Connectivity Test')}
          </h3>
          <p className="text-sm text-blue-800 mb-3">
            {t('errors.corporate.testDescription', "Use our diagnostic tool to confirm the issue and share results with IT:")}
          </p>
          <a
            href="https://selna.ai/connectivity-test/"
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors text-sm font-medium text-center"
          >
            {t('errors.corporate.testButton', 'Open Connectivity Test')} →
          </a>
        </div>
        
        {/* Contact IT */}
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-4">
          <h3 className="font-semibold text-purple-900 mb-2 flex items-center gap-2">
            <span>📧</span>
            {t('errors.corporate.contactTitle', 'Contact Your IT Department')}
          </h3>
          <p className="text-sm text-purple-800 mb-3">
            {t('errors.corporate.contactDescription', "Send a pre-filled email to your IT team with all the technical details they need:")}
          </p>
          <a
            href={generateITEmail()}
            className="block w-full px-4 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors text-sm font-medium text-center"
          >
            {t('errors.corporate.contactButton', 'Open Email to IT')} ✉️
          </a>
        </div>
        
        {/* Technical Details (collapsible) */}
        <div className="mb-6">
          <button
            onClick={() => setShowTechnicalDetails(!showTechnicalDetails)}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >
            <svg 
              className={`w-4 h-4 transition-transform ${showTechnicalDetails ? 'rotate-90' : ''}`} 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            {t('errors.corporate.technicalToggle', 'Show technical details for IT')}
          </button>
          
          {showTechnicalDetails && (
            <div className="mt-3 p-4 bg-gray-100 rounded-xl text-xs font-mono text-gray-700 space-y-1">
              <p><strong>{t('errors.corporate.required', 'Required')}:</strong></p>
              <p>• WSS (WebSocket Secure) → api.openai.com:443</p>
              <p>• HTTPS → api.openai.com:443</p>
              <p>• HTTPS → selna.ai:443</p>
              <p>• HTTPS → api.selna.ai:443</p>
              <p className="mt-2"><strong>{t('errors.corporate.note', 'Note')}:</strong></p>
              <p>{t('errors.corporate.sslNote', 'SSL inspection should be bypassed for WebSocket connections to api.openai.com')}</p>
              <p className="mt-2 text-gray-500">{t('errors.corporate.attempts', 'Connection attempts')}: {retryCount}</p>
            </div>
          )}
        </div>
        
        {/* Action Buttons */}
        <div className="space-y-3">
          <button
            onClick={onRetry}
            className="w-full px-6 py-4 bg-blue-600 text-white rounded-xl hover:bg-blue-700 active:bg-blue-800 transition-colors font-medium text-lg flex items-center justify-center gap-3 shadow-lg"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {t('errors.connection.retry', 'Try Again')}
          </button>
          
          <button
            onClick={onCancel}
            className="w-full px-6 py-3 bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200 transition-colors font-medium"
          >
            {t('common.buttons.back', 'Go Back')}
          </button>
        </div>
        
        {/* Workaround hint */}
        <p className="text-center text-sm text-gray-500 mt-4">
          {t('errors.corporate.workaround', "Workaround: Try using mobile data (hotspot) while waiting for IT to update network settings.")}
        </p>
      </div>
    </div>
  );
}

/**
 * ConnectionErrorScreen - User-friendly screen for connection issues
 * Consistent design with MicrophonePermissionScreen
 */
function ConnectionErrorScreen({
  onRetry,
  onCancel,
  retryCount
}: {
  onRetry: () => void;
  onCancel: () => void;
  retryCount: number;
}) {
  const { t } = useTranslation();
  
  // After 3+ failures, show the corporate network screen
  if (retryCount >= CORPORATE_THRESHOLD) {
    return (
      <CorporateNetworkErrorScreen
        onRetry={onRetry}
        onCancel={onCancel}
        retryCount={retryCount}
      />
    );
  }
  
  return (
    <div className="fixed inset-0 bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-6 md:p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        
        {/* Icon - Connection/Signal with issue */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-amber-100 rounded-full mb-4">
            <svg className="w-10 h-10 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
              <circle cx="12" cy="20" r="1" fill="currentColor" />
            </svg>
          </div>
          
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {t('errors.connection.title', 'Connection Issue')}
          </h2>
          
          <p className="text-gray-600 text-base">
            {t('errors.connection.description', "We couldn't establish a connection. This is usually temporary and trying again should work.")}
          </p>
        </div>
        
        {/* Helpful Tips */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
          <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
            <span>💡</span>
            {t('errors.connection.tipsTitle', 'What you can try:')}
          </h3>
          
          <ul className="space-y-2 text-sm text-blue-800">
            <li className="flex items-start gap-2">
              <span className="text-blue-500 mt-0.5">•</span>
              <span>{t('errors.connection.tip1', 'Try again - this often works on the second attempt')}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-500 mt-0.5">•</span>
              <span>{t('errors.connection.tip2', 'Check your internet connection')}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-500 mt-0.5">•</span>
              <span>{t('errors.connection.tip3', 'If using VPN or proxy, try disabling it temporarily')}</span>
            </li>
          </ul>
        </div>
        
        {/* Action Buttons */}
        <div className="space-y-3">
          <button
            onClick={onRetry}
            className="w-full px-6 py-4 bg-blue-600 text-white rounded-xl hover:bg-blue-700 active:bg-blue-800 transition-colors font-medium text-lg flex items-center justify-center gap-3 shadow-lg"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {t('errors.connection.retry', 'Try Again')}
          </button>
          
          <button
            onClick={onCancel}
            className="w-full px-6 py-3 bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200 transition-colors font-medium"
          >
            {t('common.buttons.back', 'Go Back')}
          </button>
        </div>
        
        {/* Reassurance text */}
        <p className="text-center text-sm text-gray-500 mt-4">
          {t('errors.connection.reassurance', "This issue is usually temporary. Most users succeed on retry.")}
        </p>
      </div>
    </div>
  );
}

export default function ErrorState({ 
  error, 
  onRetry, 
  onCancel,
  allowKeyboardInput = true,
  onContinueKeyboard
}: ErrorStateProps) {
  const { t } = useTranslation();
  const [urlCopied, setUrlCopied] = useState(false);
  const [connectionRetryCount, setConnectionRetryCount] = useState(0);
  
  // Extract error details
  const errorMessage = error.message;
  const statusCode = (error as any).statusCode || error.statusCode;
  
  // Determine error types
  const isRateLimitError = statusCode === 429 || errorMessage.includes('429') || errorMessage.includes('rate limit');
  const isMicrophonePermissionError = errorMessage.toLowerCase().includes('permission') ||
                                       errorMessage.toLowerCase().includes('denied') ||
                                       errorMessage.toLowerCase().includes('notallowederror') ||
                                       errorMessage.toLowerCase().includes('not allowed');
  const isMicrophoneError = isMicrophonePermissionError ||
                            errorMessage.toLowerCase().includes('microphone');
  const isHTTPSError = errorMessage.includes('HTTPS');
  const isMicInUseError = errorMessage.includes('in use by another application');
  const isNoMicError = errorMessage.includes('No microphone found');
  
  // Detect if user is on mobile
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  
  // Detect if user is in a webview
  const isWebView = isInWebView();
  
  // Track connection errors - increment counter when this component mounts with a connection error
  useEffect(() => {
    if (isConnectionError(error) && !isMicrophoneError && !isHTTPSError) {
      const newCount = incrementConnectionRetryCount();
      setConnectionRetryCount(newCount);
    }
  }, [error]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Wrap onRetry to track attempts (count is incremented on mount, not on retry click)
  const handleRetry = () => {
    onRetry();
  };
  
  // Wrap onCancel to reset counter on successful navigation away
  const handleCancel = () => {
    // Don't reset here - user might come back and try again
    // Only reset after successful connection (handled elsewhere)
    onCancel();
  };
  
  // 🌐 Show dedicated connection error screen for WebSocket/connection errors
  if (isConnectionError(error) && !isMicrophoneError && !isHTTPSError) {
    return (
      <ConnectionErrorScreen
        onRetry={handleRetry}
        onCancel={handleCancel}
        retryCount={connectionRetryCount}
      />
    );
  }
  
  // 🎤 Show dedicated microphone permission screen for permission errors (not HTTPS, not in-use, not no-mic)
  if (isMicrophonePermissionError && !isHTTPSError && !isMicInUseError && !isNoMicError && !isWebView) {
    return (
      <MicrophonePermissionScreen
        onRetry={onRetry}
        onCancel={onCancel}
        allowKeyboardInput={allowKeyboardInput}
        onContinueKeyboard={onContinueKeyboard}
      />
    );
  }
  
  // Handle copy URL
  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 3000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-6 md:p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="text-center mb-6">
          {/* Icon */}
          <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-4">
            <svg className="w-10 h-10 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          
          <h3 className="text-2xl font-bold text-gray-900 mb-2">{t('errors.sessionError')}</h3>
          <p className="text-gray-600 mb-3">{errorMessage}</p>
          
          {/* Additional context for rate limit errors */}
          {isRateLimitError && (
            <div className="mt-3 p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
              💡 {t('errors.rateLimitOpenAI')}
            </div>
          )}
          
          {/* WebView detection - show this FIRST if user is in webview with mic error */}
          {isMicrophoneError && isWebView && !isHTTPSError && (
            <div className="mt-4 p-4 bg-purple-50 border border-purple-200 rounded-xl text-left">
              <h4 className="font-semibold text-purple-900 mb-2 text-sm">📱 {t('errors.webViewDetected.title')}</h4>
              <p className="text-sm text-purple-800 mb-3">
                {t('errors.webViewDetected.description')}
              </p>
              <button
                onClick={handleCopyUrl}
                className="w-full px-4 py-2 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors text-sm font-medium"
              >
                {urlCopied ? t('errors.webViewDetected.copied') : t('errors.webViewDetected.copyButton')}
              </button>
            </div>
          )}
          
          {/* Microphone permission help - only show if NOT in webview */}
          {isMicrophoneError && !isWebView && !isHTTPSError && !isMicInUseError && !isNoMicError && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-xl text-left">
              <h4 className="font-semibold text-blue-900 mb-2 text-sm">🎤 {t('errors.microphoneHelp.title')}</h4>
              {isMobile ? (
                <ul className="text-sm text-blue-800 space-y-2">
                  <li>• <strong>Android:</strong> {t('errors.microphoneHelp.android', { hostname: window.location.hostname })}</li>
                  <li>• <strong>iPhone:</strong> {t('errors.microphoneHelp.iphone')}</li>
                  <li>• {t('errors.microphoneHelp.reload')}</li>
                </ul>
              ) : (
                <ul className="text-sm text-blue-800 space-y-2">
                  <li>• <strong>Chrome:</strong> {t('errors.microphoneHelp.chrome')}</li>
                  <li>• <strong>Safari:</strong> {t('errors.microphoneHelp.safari')}</li>
                  <li>• <strong>Firefox:</strong> {t('errors.microphoneHelp.firefox')}</li>
                </ul>
              )}
            </div>
          )}
          
          {/* Microphone in use help */}
          {isMicInUseError && (
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl text-left">
              <h4 className="font-semibold text-amber-900 mb-2 text-sm">⚠️ {t('errors.micInUse.title')}</h4>
              <ul className="text-sm text-amber-800 space-y-1">
                <li>• {t('errors.micInUse.closeApps')}</li>
                <li>• {t('errors.micInUse.closeTabs')}</li>
                <li>• {t('errors.micInUse.tryAgain')}</li>
              </ul>
            </div>
          )}
          
          {/* No microphone found help */}
          {isNoMicError && (
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl text-left">
              <h4 className="font-semibold text-amber-900 mb-2 text-sm">🎤 {t('errors.noMic.title')}</h4>
              <ul className="text-sm text-amber-800 space-y-1">
                <li>• {t('errors.noMic.checkPlugged')}</li>
                <li>• {t('errors.noMic.checkEnabled')}</li>
                <li>• {t('errors.noMic.tryDifferent')}</li>
              </ul>
            </div>
          )}
          
          {/* HTTPS requirement help */}
          {isHTTPSError && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl text-left">
              <h4 className="font-semibold text-red-900 mb-2 text-sm">🔒 {t('errors.httpsRequired.title')}</h4>
              <p className="text-sm text-red-800">
                {t('errors.httpsRequired.description')}
              </p>
            </div>
          )}
        </div>
        
        <div className="space-y-3">
          <button
            onClick={onRetry}
            className="w-full px-6 py-4 bg-blue-600 text-white rounded-xl hover:bg-blue-700 active:bg-blue-800 transition-colors font-medium text-lg flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {t('common.buttons.retry')}
          </button>
          <button
            onClick={onCancel}
            className="w-full px-6 py-3 bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200 transition-colors font-medium"
          >
            {t('common.buttons.back')}
          </button>
        </div>
      </div>
    </div>
  );
}

